import type { UserFavoriteTab } from '../../../services/favoriteTabsService';

export const NON_WORKSPACE_PATHS = new Set(['/', '/login', '/signup']);

export function normalizeEntryPath(path: string): string {
  const trimmed = path.replace(/\/$/, '');
  return trimmed || '/';
}

export function isWorkspaceRoute(path: string): boolean {
  const normalized = normalizeEntryPath(path);
  return normalized.length > 0 && !NON_WORKSPACE_PATHS.has(normalized);
}

export function shouldAwaitFavoritesForEntry(pathname: string): boolean {
  return NON_WORKSPACE_PATHS.has(normalizeEntryPath(pathname));
}

export function normalizeAccessibleFavorites(
  favorites: UserFavoriteTab[],
  canAccess: (path: string) => boolean,
): UserFavoriteTab[] {
  const seenPaths = new Set<string>();

  return [...favorites]
    .sort(
      (a, b) =>
        a.order_index - b.order_index ||
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime() ||
        a.id.localeCompare(b.id),
    )
    .filter((favorite) => {
      if (!favorite.path || seenPaths.has(favorite.path) || !canAccess(favorite.path)) {
        return false;
      }
      seenPaths.add(favorite.path);
      return true;
    });
}

export type WorkspaceEntryInput = {
  pathname: string;
  favorites: UserFavoriteTab[];
  restoredActivePath: string | null;
  defaultAccessiblePath: string;
  canAccessPath: (path: string) => boolean;
  allowFavoritesEntry: boolean;
  isWorkspaceRoute?: (path: string) => boolean;
};

export function resolveWorkspaceEntryPath(input: WorkspaceEntryInput): string {
  const {
    pathname,
    favorites,
    restoredActivePath,
    defaultAccessiblePath,
    canAccessPath,
    allowFavoritesEntry,
    isWorkspaceRoute: isWorkspaceRouteFn = isWorkspaceRoute,
  } = input;

  if (isWorkspaceRouteFn(pathname)) {
    return canAccessPath(pathname) ? pathname : defaultAccessiblePath;
  }

  if (allowFavoritesEntry && favorites.length > 0) {
    return favorites[0].path;
  }

  return restoredActivePath ?? defaultAccessiblePath;
}
